const US_STATES: Record<string, string> = {
  AL: "alabama",
  AK: "alaska",
  AZ: "arizona",
  AR: "arkansas",
  CA: "california",
  CO: "colorado",
  CT: "connecticut",
  DE: "delaware",
  DC: "district of columbia",
  FL: "florida",
  GA: "georgia",
  HI: "hawaii",
  ID: "idaho",
  IL: "illinois",
  IN: "indiana",
  IA: "iowa",
  KS: "kansas",
  KY: "kentucky",
  LA: "louisiana",
  ME: "maine",
  MD: "maryland",
  MA: "massachusetts",
  MI: "michigan",
  MN: "minnesota",
  MS: "mississippi",
  MO: "missouri",
  MT: "montana",
  NE: "nebraska",
  NV: "nevada",
  NH: "new hampshire",
  NJ: "new jersey",
  NM: "new mexico",
  NY: "new york",
  NC: "north carolina",
  ND: "north dakota",
  OH: "ohio",
  OK: "oklahoma",
  OR: "oregon",
  PA: "pennsylvania",
  RI: "rhode island",
  SC: "south carolina",
  SD: "south dakota",
  TN: "tennessee",
  TX: "texas",
  UT: "utah",
  VT: "vermont",
  VA: "virginia",
  WA: "washington",
  WV: "west virginia",
  WI: "wisconsin",
  WY: "wyoming",
  AS: "american samoa",
  GU: "guam",
  MP: "northern mariana islands",
  PR: "puerto rico",
  VI: "virgin islands",
  AA: "armed forces americas",
  AE: "armed forces europe",
  AP: "armed forces pacific",
};

const normalize = (value?: string) =>
  (value ?? "").toLowerCase().replace(/[.,#]/g, "").replace(/\s+/g, " ").trim();

/** Returns the two-letter code for a state name or abbreviation, if valid */
const toStateCode = (value?: string) => {
  const normalized = normalize(value);
  const upper = normalized.toUpperCase();
  if (US_STATES[upper]) return upper;
  return Object.keys(US_STATES).find((code) => US_STATES[code] === normalized);
};

export const formatAddressLine = (address: IAddressParts) =>
  [
    address.address_1,
    address.address_2,
    address.city,
    [address.state, address.zip].filter(Boolean).join(" "),
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

const joinComponents = (
  components: IAddressValidationResult["components"],
  labels: UsaddressLabel[]
) =>
  labels
    .map((label) => components[label])
    .filter(Boolean)
    .join(" ");

/** Maps usaddress components onto the patient address fields */
export const toStandardizedAddress = (
  components: IAddressValidationResult["components"]
): IAddressParts => ({
  address_1: joinComponents(components, [
    "AddressNumberPrefix",
    "AddressNumber",
    "AddressNumberSuffix",
  ]),
  address_2: joinComponents(components, [
    "StreetNamePreModifier",
    "StreetNamePreDirectional",
    "StreetNamePreType",
    "StreetName",
    "StreetNamePostType",
    "StreetNamePostDirectional",
    "OccupancyType",
    "OccupancyIdentifier",
  ]),
  city: components.PlaceName ?? "",
  state: toStateCode(components.StateName) ?? components.StateName ?? "",
  zip:
    components.ZipCode && components.ZipPlus4
      ? `${components.ZipCode}-${components.ZipPlus4}`
      : components.ZipCode ?? "",
});

const collectIssues = (
  address: IAddressParts,
  addressType: UsaddressType,
  components: IAddressValidationResult["components"]
) => {
  const issues: string[] = [];

  if (addressType === "Street Address") {
    if (!components.StreetName) issues.push("Missing street name");
  } else if (addressType === "PO Box") {
    if (!components.USPSBoxID) issues.push("Missing PO Box number");
  } else if (addressType === "Intersection") {
    issues.push("Address is an intersection, not a deliverable address");
  } else {
    issues.push("Missing house number or PO Box");
  }

  if (components.NotAddress) {
    issues.push(`Unrecognized text: "${components.NotAddress}"`);
  }

  if (!components.PlaceName) {
    issues.push("Missing city");
  } else if (
    address.city &&
    normalize(components.PlaceName) !== normalize(address.city)
  ) {
    issues.push(
      `City was read as "${components.PlaceName}" instead of "${address.city}"`
    );
  }

  const stateCode = toStateCode(components.StateName);
  if (!components.StateName) {
    issues.push("Missing state");
  } else if (!stateCode) {
    issues.push(`"${components.StateName}" is not a US state`);
  } else if (address.state && toStateCode(address.state) !== stateCode) {
    issues.push(
      `State was read as "${components.StateName}" instead of "${address.state}"`
    );
  }

  const zip = components.ZipCode;
  if (!zip) {
    issues.push("Missing ZIP code");
  } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
    issues.push(`"${zip}" is not a valid ZIP code`);
  } else if (
    address.zip &&
    zip.slice(0, 5) !== address.zip.trim().slice(0, 5)
  ) {
    issues.push(`ZIP was read as "${zip}" instead of "${address.zip}"`);
  }
  if (components.ZipPlus4 && !/^\d{4}$/.test(components.ZipPlus4)) {
    issues.push(`"${components.ZipPlus4}" is not a valid ZIP+4 extension`);
  }

  return issues;
};

export const AddressValidationServiceImplFactory = ({
  usaddressProvider,
}: {
  usaddressProvider: IUsaddressProvider;
}): IAddressValidationService => {
  const validateAddress: IValidateAddress = async (address) => {
    const line = formatAddressLine(address);
    if (!line) {
      return {
        success: true,
        data: {
          isValid: false,
          address: line,
          components: {},
          issues: ["Address is empty"],
        },
      };
    }

    try {
      const result = await usaddressProvider.tag(line);

      if (!result.ok) {
        return {
          success: true,
          data: {
            isValid: false,
            address: line,
            components: {},
            tokens: result.tokens,
            issues: [
              "Address could not be parsed: the same part appears more than once",
            ],
          },
        };
      }

      const issues = collectIssues(
        address,
        result.addressType,
        result.components
      );
      return {
        success: true,
        data: {
          isValid: issues.length === 0,
          address: line,
          addressType: result.addressType,
          components: result.components,
          standardized: toStandardizedAddress(result.components),
          issues,
        },
      };
    } catch (error) {
      console.error("[AddressValidation] usaddress failed", error);
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  return { validateAddress };
};
